import { createSlice } from "@reduxjs/toolkit";
import { sharedInitialState, sharedReducers } from "./shared";
import type { SharedLoginState } from "./shared";
// IMPORTED as well as re-exported below: this file's own `extraReducers` case on them, and an
// `export ... from` creates no local binding (ledger rule 5 / hazard 30).
import { supportLogout, userLogout } from "./shared";
import { getLandlineCountriesFromPicklist } from "../../../phoneCountry";
import type { PhoneCountry } from "../../../phoneCountry";

/**
 * The two logout actions moved to `./shared.ts` so both platforms can declare the
 * `extraReducers` that listen for them — see there for why that is safe, and for why creating
 * them on mobile is inert. Re-exported here because 26 web slices import them from THIS path;
 * one definition, and not one of those imports changed.
 */
export { userLogout, supportLogout };

export interface LoginState extends SharedLoginState {
  updateData: Record<string, any>,
  catalogFetchingInProgress: boolean,
  verifyPinResponse: Record<string, any>,
  masterDataPicklist: {
    [key: string]: any
  },
  // Derived from `masterDataPicklist`: only countries with a `landline` entry, carrying its min/max/prefixes.
  landlineNumberDetails: PhoneCountry[],
  isMasterDataPicklistFetching: boolean
}

const initialState: LoginState = {
  ...sharedInitialState,
  updateData: {},
  catalogFetchingInProgress: false,
  verifyPinResponse: {},
  masterDataPicklist: {},
  landlineNumberDetails: [],
  isMasterDataPicklistFetching: false,
};

export const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    ...sharedReducers,
    // Diverges from mobile, which additionally sets `isLoginDone`.
    getLoginResponseSuccess: (state, action) => {
      state.loginResponse = action.payload;
      state.isLoginDataFetching = false;
    },
    // The 12 below are web-only.
    fetchCompleteCatalogData: (state, action) => {
      state.catalogFetchingInProgress = true;
    },
    fetchCompleteCatalogDataSuccess: (state) => {
      state.catalogFetchingInProgress = false;
    },
    fetchOfflineUsersCallComplete: (state) => {
      // this action is to tell that the fetch offline user call has completed
    },
    updateLoginSlice: (state, action) => {
      return action.payload;
    },
    getLatestVersion: (state, action) => {

    },
    saveFetchLastestVersionData: (state, action) => {
      console.log(action.payload)
      state.updateData = action.payload
    },
    verifyPinRequest: (state, action) => {
    },
    verifyPinSuccess: (state, action) => {
      state.verifyPinResponse = action.payload;
    },
    verifyPinFailure: (state) => {
    },
    resetVerifyPinResponse: (state) => {
      state.verifyPinResponse = {};
    },
    fetchMasterDataPicklist: (state, action) => {
      state.isMasterDataPicklistFetching = true;
    },
    saveMasterDataPicklistSuccess: (state, action) => {
      state.masterDataPicklist = action.payload;
      state.landlineNumberDetails = getLandlineCountriesFromPicklist(action.payload);
      state.isMasterDataPicklistFetching = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(userLogout, (state) => {
        state.logoutResponse = {};
        state.loginResponse = {};
      })
      .addCase(supportLogout, (state) => {
        state.logoutResponse = {};
        state.loginResponse = {};
      });
  },
});

export const {
  getLoginRespoonse,
  getLoginResponseSuccess,
  getActiveUsers,
  getActiveUsersSuccess,
  logoutUser,
  logoutUserSuccess,
  forgotPinFirstResponse,
  forgotPinFirstResponseSuccess,
  emptyForgotPinFirstResponse,
  validateEmail,
  validateEmailSuccess,
  emptyVaildateEmailRes,
  switchUser,
  switchUserSuccess,
  resetSwitchUser,
  resetLogout,
  showLogoutToast,
  hideLogoutToast,
  clearLoginResponse,
  setLastLoggedInUser,
  removeLastLoggedInUser,
  fetchOfflineUsers,
  fetchOfflineUsersSuccess,
  openAccountControl,
  closeAccountControl,
  updateLoginSlice,
  getLatestVersion,
  saveFetchLastestVersionData,
  fetchCompleteCatalogData,
  fetchCompleteCatalogDataSuccess,
  fetchOfflineUsersCallComplete,
  verifyPinRequest,
  verifyPinSuccess,
  verifyPinFailure,
  resetVerifyPinResponse,
  fetchMasterDataPicklist,
  saveMasterDataPicklistSuccess,
  setPhoneCountries,
  setPhoneCountriesError,
} = loginSlice.actions;

export default loginSlice.reducer;
